import { FormEvent, useState } from 'react';
import Money from './Money';
import InfoTip from './InfoTip';
import { financeApi } from '../../services/financeApi';
import { FinancePeriodEntry, FinancePeriodEntryType, ProviderVatReconciliation } from '../../types/finance';

interface ProviderVatReconcileProps {
  period: string;
  reconciliation: ProviderVatReconciliation;
  /** The PAYMOB_INPUT_VAT figure currently in Box 9 (0 when nothing entered). */
  paymobInputVat: number;
  entries: FinancePeriodEntry[];
  token: string;
  /** Called after a successful save so the parent refetches the report + entries. */
  onSaved: () => void;
}

interface EntryFormProps {
  period: string;
  type: FinancePeriodEntryType;
  title: string;
  amountLabel: string;
  documentRequired: boolean;
  existing: FinancePeriodEntry | undefined;
  token: string;
  onSaved: () => void;
}

function EntryForm({ period, type, title, amountLabel, documentRequired, existing, token, onSaved }: EntryFormProps) {
  const [amount, setAmount] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [inputKey, setInputKey] = useState(0);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const parsed = Number(amount);
    if (!Number.isFinite(parsed) || parsed < 0) { setError('Enter the VAT amount from the invoice'); return; }
    if (documentRequired && !file) { setError('Attach the provider invoice'); return; }
    setSaving(true);
    setError(null);
    try {
      await financeApi.createPeriodEntry({ period, type, amount: parsed, document: file }, token);
      setAmount('');
      setFile(null);
      setInputKey((k) => k + 1);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} aria-label={title} style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
      <div style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--admin-text)' }}>{title}</div>
      {existing && (
        <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>
          Entered {new Date(existing.createdAt).toLocaleDateString()}
          {existing.enteredByName?.trim() && <span data-testid="entered-by"> by {existing.enteredByName.trim()}</span>}
          {existing.documentUrl && (
            <>
              {' · '}
              <a href={existing.documentUrl} target="_blank" rel="noopener noreferrer">View invoice</a>
            </>
          )}
          {' · '}saving again replaces it
        </div>
      )}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <div className="admin-field" style={{ margin: 0 }}>
          <label className="admin-field-label" htmlFor={`${type}-amount`}>{amountLabel}</label>
          <input
            id={`${type}-amount`}
            type="number"
            step="0.01"
            min="0"
            inputMode="decimal"
            className="admin-search-input"
            style={{ width: '10rem' }}
            value={amount}
            disabled={saving}
            onChange={(e) => setAmount(e.target.value)}
          />
        </div>
        <div className="admin-field" style={{ margin: 0 }}>
          <label className="admin-field-label" htmlFor={`${type}-document`}>
            Invoice {documentRequired ? '(required)' : '(optional)'}
          </label>
          <input
            key={inputKey}
            id={`${type}-document`}
            type="file"
            accept=".pdf,image/*"
            disabled={saving}
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </div>
        <button type="submit" className="admin-btn admin-btn-sm admin-btn-primary" disabled={saving}>
          {saving ? 'Saving…' : existing ? 'Replace' : 'Save'}
        </button>
      </div>
      {error && <div style={{ fontSize: '0.75rem', color: 'var(--admin-danger)' }}>{error}</div>}
    </form>
  );
}

/**
 * Spec 053 AC-13b — derived Stripe fee VAT vs the VAT total on Stripe's monthly invoice, plus the
 * manual Paymob input VAT entry. Both entries are `finance_period_entries` rows (UNIQUE period+type).
 */
export default function ProviderVatReconcile({ period, reconciliation, paymobInputVat, entries, token, onSaved }: ProviderVatReconcileProps) {
  const stripeEntry = entries.find((e) => e.type === 'STRIPE_VAT_INVOICE');
  const paymobEntry = entries.find((e) => e.type === 'PAYMOB_INPUT_VAT');
  const { derived, invoiced, variance, flagged } = reconciliation;

  return (
    <div className="admin-card" style={{ marginTop: '1rem' }}>
      <div className="admin-card-header">
        <h3 className="admin-card-title">Provider VAT reconciliation</h3>
        {invoiced === null ? (
          <span className="admin-badge pending">Invoice not attached</span>
        ) : flagged ? (
          <span className="admin-badge rejected" data-testid="variance-flag">Variance ≥ AED 1.00 — review</span>
        ) : (
          <span className="admin-badge approved" data-testid="variance-ok">Reconciled</span>
        )}
      </div>

      <div className="admin-grid-3" style={{ marginBottom: '1rem' }}>
        <div>
          <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>Derived from charges</div>
          <Money amount={derived} glossaryKey="providerVatDerived" style={{ fontSize: '1.125rem', fontWeight: 600 }} />
        </div>
        <div>
          <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>Stripe invoice total</div>
          <Money amount={invoiced} glossaryKey="providerVatInvoiced" style={{ fontSize: '1.125rem', fontWeight: 600 }} />
        </div>
        <div>
          <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>Variance</div>
          <Money
            amount={variance}
            glossaryKey="providerVatVariance"
            style={{ fontSize: '1.125rem', fontWeight: 600, color: flagged ? 'var(--admin-danger)' : undefined }}
          />
        </div>
      </div>

      <div className="admin-two-col" style={{ gap: '1.5rem', borderTop: '1px solid var(--admin-border)', paddingTop: '1rem' }}>
        <EntryForm
          period={period}
          type="STRIPE_VAT_INVOICE"
          title="Stripe monthly VAT invoice"
          amountLabel="VAT total on invoice (AED)"
          documentRequired
          existing={stripeEntry}
          token={token}
          onSaved={onSaved}
        />
        <div>
          <div style={{ fontSize: '0.75rem', color: 'var(--admin-text-muted)', marginBottom: '0.25rem' }}>
            Currently in Box 9
            <InfoTip glossaryKey="paymobInputVat" />
          </div>
          <Money amount={paymobInputVat} glossaryKey="paymobInputVat" hideTip style={{ fontWeight: 600, marginBottom: '0.75rem' }} />
          <EntryForm
            period={period}
            type="PAYMOB_INPUT_VAT"
            title="Paymob input VAT"
            amountLabel="Input VAT (AED)"
            documentRequired={false}
            existing={paymobEntry}
            token={token}
            onSaved={onSaved}
          />
        </div>
      </div>
    </div>
  );
}
